import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ClientLayout from '../../components/ClientLayout';
import ExpertSearchBar from '../../components/client/ExpertSearchBar';
import ExpertCard from '../../components/client/ExpertCard';
import NextBookingsSection from '../../components/client/NextBookingsSection';
import { clientPortalApi, ExpertSummary } from '../../lib/http';

const ClientHome: React.FC = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<ExpertSummary[]>([]);
  const [addedExperts, setAddedExperts] = useState<ExpertSummary[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(true);
  const [isLoadingAdded, setIsLoadingAdded] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchFavorites();
    fetchAddedExperts();
  }, []);

  const fetchFavorites = async () => {
    try {
      setIsLoadingFavorites(true);
      const response = await clientPortalApi.favorites.list();
      setFavorites(response.data);
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
      setError('Failed to load favorites');
    } finally {
      setIsLoadingFavorites(false);
    }
  };

  const fetchAddedExperts = async () => {
    try {
      setIsLoadingAdded(true);
      const response = await clientPortalApi.experts.listAdded();
      setAddedExperts(response.data);
    } catch (err) {
      console.error('Failed to fetch added experts:', err);
      setError('Failed to load experts');
    } finally {
      setIsLoadingAdded(false);
    }
  };

  const handleFavoriteToggle = (expertSlug: string, isFavorite: boolean) => {
    // Refresh both lists to reflect the change
    fetchFavorites();
    fetchAddedExperts();
  };

  const handleExpertClick = (slug: string) => {
    navigate(`/u/experts/${slug}`);
  };

  return (
    <ClientLayout>
      <div className="space-y-5">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-2xl flex items-center justify-between text-sm">
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-600 p-1"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}

        {/* My Next Bookings Section */}
        <NextBookingsSection />

        {/* Search Bar */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-3">
            Find Experts
          </h2>
          <ExpertSearchBar />
        </section>

        {/* Favorites Section */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">
              Favorites
            </h2>
            <button
              onClick={() => navigate('/u/favorites')}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium transition-colors px-2 py-1 hover:bg-purple-50 rounded-lg cursor-pointer"
            >
              View All
            </button>
          </div>

          {isLoadingFavorites ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-[72px] bg-gray-50 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-900">No favorites yet</p>
              <p className="text-xs text-gray-500 mt-1">Tap the heart on any expert to add them here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {favorites.map((expert) => (
                <ExpertCard
                  key={expert.slug}
                  slug={expert.slug}
                  displayName={expert.displayName}
                  isFavorite={expert.isFavorite !== undefined ? expert.isFavorite : true}
                  subtitle="Favorite"
                  onClick={handleExpertClick}
                  onFavoriteToggle={(isFav) => handleFavoriteToggle(expert.slug, isFav)}
                />
              ))}
            </div>
          )}
        </section>

        {/* My Experts Section */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">
              My Experts
            </h2>
            <button
              onClick={() => navigate('/u/my-experts')}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium transition-colors px-2 py-1 hover:bg-purple-50 rounded-lg cursor-pointer"
            >
              View All
            </button>
          </div>

          {isLoadingAdded ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-[72px] bg-gray-50 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : addedExperts.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-900">No experts yet</p>
              <p className="text-xs text-gray-500 mt-1">Book with experts to see them here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {addedExperts.map((expert) => (
                <ExpertCard
                  key={expert.slug}
                  slug={expert.slug}
                  displayName={expert.displayName}
                  isFavorite={expert.isFavorite !== undefined ? expert.isFavorite : false}
                  subtitle="Your expert"
                  onClick={handleExpertClick}
                  onFavoriteToggle={(isFav) => handleFavoriteToggle(expert.slug, isFav)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </ClientLayout>
  );
};

export default ClientHome;
